import React from 'react';

import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Collapse, IconButton, Link as MuiLink, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import { formatDateSlashWithTime } from '~/services/TimeService';

import type { ContractingQueueCase } from '~/types/services/contracting';

import CaseClampedText from './CaseClampedText';
import CaseEmptyRow from './CaseEmptyRow';
import CaseExpandedDetails from './CaseExpandedDetails';
import { useExpandedCaseRows } from './useExpandedCaseRows';

// Widths sum to 100% so the table fits the page; long text clamps instead of scrolling.
// Short Detail, Customers Email and Case Owner appear only in the expanded row.
const EXPAND_COLUMN_WIDTH = '3%';
const COLUMNS = [
  { label: 'Case Number', width: '9%' },
  { label: 'Subject', width: '35%' },
  { label: 'Status', width: '16%' },
  { label: 'Date/Time Opened', width: '12%' },
  { label: 'Contact Account Name', width: '16%' },
  { label: 'Zendesk Id', width: '9%' },
];
const TOTAL_COLUMNS = COLUMNS.length + 1;

const headerCellSx = { fontWeight: 'bold', color: 'primary.main', verticalAlign: 'bottom' } as const;

interface Props {
  cases: Array<ContractingQueueCase>;
  buildCaseHref: (caseNumber: string) => string;
}

export default function CasesAllPage({ cases, buildCaseHref }: Props) {
  const { isExpanded, toggle, handleRowClick } = useExpandedCaseRows();

  return (
    <Table aria-label="All campaign operation cases" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <colgroup>
        <col style={{ width: EXPAND_COLUMN_WIDTH }} />
        {COLUMNS.map((column) => (
          <col key={column.label} style={{ width: column.width }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell />
          {COLUMNS.map((column) => (
            <TableCell key={column.label} sx={headerCellSx}>
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {cases.length === 0 && <CaseEmptyRow colSpan={TOTAL_COLUMNS} message="No cases to show." />}
        {cases.map((item) => {
          const isOpen = isExpanded(item.case_number);

          return (
            <React.Fragment key={item.case_number}>
              <TableRow
                hover
                onClick={(event) => handleRowClick(event, item.case_number)}
                sx={{ verticalAlign: 'top', cursor: 'pointer', '& > td': isOpen ? { borderBottom: 0 } : undefined }}
              >
                <TableCell padding="none" align="center" sx={{ verticalAlign: 'middle' }}>
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    aria-label={isOpen ? `Collapse case ${item.case_number}` : `Expand case ${item.case_number}`}
                    aria-expanded={isOpen}
                    onClick={() => toggle(item.case_number)}
                  >
                    {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <MuiLink component={Link} to={buildCaseHref(item.case_number)} underline="hover">
                    {item.case_number}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.subject} maxLines={3}>
                    <MuiLink component={Link} to={buildCaseHref(item.case_number)} underline="hover">
                      {item.subject}
                    </MuiLink>
                  </CaseClampedText>
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.status} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={formatDateSlashWithTime(item.created_at)} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.contact_account?.name ?? null} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.zendesk_id} maxLines={1} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={TOTAL_COLUMNS} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                  <Collapse in={isOpen} unmountOnExit>
                    <CaseExpandedDetails caseItem={item} caseHref={buildCaseHref(item.case_number)} />
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
